import type { Tweaks } from "./types";
import { slugifyPageName } from "./customWebsitePages";

/**
 * Built-in pages whose first URL segment can be renamed via `nav.*.href`
 * (Pages rail / Site Header Link field). Values are the canonical public roots.
 */
const BUILTIN_PAGE_PUBLIC_ROOTS: Record<string, string> = {
  content: "content",
  shop: "shop",
  about: "about",
  contact: "contact",
  press: "press",
  newsletter: "newsletter",
  search: "search",
  linkInBio: "link-in-bio",
  blog: "blog",
  journal: "blog",
};

function navKeyForBuiltinPage(pageKey: string): string {
  if (pageKey === "journal" || pageKey === "journalPost") return "blog";
  return pageKey;
}

function decodeSeg(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** Live public root for a built-in page (`content`, `hello-ganika`, …). */
export function builtinPagePublicRoot(
  tweaks: Tweaks | null | undefined,
  pageKey: string,
): string {
  const navKey = navKeyForBuiltinPage(pageKey);
  const fallback =
    BUILTIN_PAGE_PUBLIC_ROOTS[navKey] ||
    BUILTIN_PAGE_PUBLIC_ROOTS[pageKey] ||
    "";
  if (!fallback) return fallback;
  const edits = (tweaks?.contentEdits || {}) as Record<
    string,
    string | null | undefined
  >;
  const fromHref = String(edits[`nav.${navKey}.href`] ?? "").trim();
  const fromSlug = String(edits[`nav.slug.${navKey}`] ?? "").trim();
  const raw = (fromHref || fromSlug).replace(/^\//, "").split("/")[0] || "";
  const live = slugifyPageName(raw);
  return live || fallback;
}

/**
 * If the first path segment is a renamed built-in slug, rewrite to the
 * canonical root so existing parsers (`content/...`, `shop/...`) keep working.
 */
export function rewritePublicPathForBuiltinSlugAliases(
  pathRest: string,
  tweaks?: Tweaks | null,
): string {
  const cleaned = String(pathRest || "")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
  if (!cleaned || !tweaks) return cleaned;
  const parts = cleaned.split("/").filter(Boolean);
  // Slugify so a copied Link with a trailing `-` still matches the live root.
  const first = slugifyPageName(decodeSeg(parts[0] || "")).toLowerCase();
  if (!first) return cleaned;

  for (const [pageKey, canonical] of Object.entries(BUILTIN_PAGE_PUBLIC_ROOTS)) {
    // Prefer pageKey entries once (blog/journal share "blog").
    if (pageKey === "journal") continue;
    const live = builtinPagePublicRoot(tweaks, pageKey).toLowerCase();
    if (live === first && first !== canonical) {
      return [canonical, ...parts.slice(1)].join("/");
    }
  }
  return cleaned;
}

/** Built-in pageKey whose live public root equals `segment`, if any. */
export function matchBuiltinPageKeyByPublicRoot(
  tweaks: Tweaks | null | undefined,
  segment: string,
): string | null {
  const seg = slugifyPageName(segment);
  if (!seg || !tweaks) return null;
  for (const pageKey of Object.keys(BUILTIN_PAGE_PUBLIC_ROOTS)) {
    if (pageKey === "journal") continue;
    if (builtinPagePublicRoot(tweaks, pageKey).toLowerCase() === seg) {
      return pageKey === "blog" ? "journal" : pageKey;
    }
  }
  return null;
}
